import { Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import React from 'react';
import { formatPercentage, uuidv4 } from '../../../../../common';
import { LoadingIndicator } from '../../../../../common/components/LoadingIndicator';
import { getValueColor, TABLE_PAPER_ELEVATION, TABLE_PAPER_SX } from '../../../../../common/components/dataTable/tableStyles';
import { LoadingProgress } from '../../../../../common/store/types';
import { PerformanceParameters, PeriodicSummaryTableResult } from '../store/types';

export interface PeriodicSummaryTableProps {
  parameters: PerformanceParameters;
  periodicSummaryTableProgress: LoadingProgress;
  periodicSummaryTableResults?: PeriodicSummaryTableResult;
}

const sectionHeaderCellSx = {
  padding: '8px 8px !important',
  backgroundColor: '#CACACA !important',
  border: 'none',
};

const getBodyRowSx = (index: number) => ({
  backgroundColor: index % 2 === 0 ? '#E9E9E9' : '#F4F4F4',
  '& td, & th': {
    padding: '8px 8px',
  },
});

export const PeriodicSummaryTable = (props: PeriodicSummaryTableProps): JSX.Element => {
  const { parameters, periodicSummaryTableResults, periodicSummaryTableProgress } = props;
  const benchmark = parameters?.selectedBenchmark;

  const results = periodicSummaryTableResults?.seriesData;
  const rows = results ?? [];

  return (
    <LoadingIndicator progress={periodicSummaryTableProgress}>
      <Paper elevation={TABLE_PAPER_ELEVATION} sx={TABLE_PAPER_SX}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={sectionHeaderCellSx} style={{ padding: '0' }} />
              <TableCell sx={sectionHeaderCellSx} colSpan={3}>
                <Typography variant="h3" color={'primary'}>
                  {parameters?.selectedPortfolio?.investmentServiceName}
                </Typography>
              </TableCell>
              <TableCell sx={sectionHeaderCellSx} colSpan={3}>
                <Typography variant="h3" color={'primary'}>
                  {benchmark ? `${benchmark?.code} - ${benchmark?.name}` : 'No benchmark selected'}
                </Typography>
              </TableCell>
              <TableCell sx={sectionHeaderCellSx} colSpan={3}>
                <Typography variant="h3" color={'primary'}>
                  Portfolio Vs Benchmark
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell variant={'head'} style={{ padding: '0' }} />
              <TableCell variant={'head'}>CAPITAL</TableCell>
              <TableCell variant={'head'}>INCOME</TableCell>
              <TableCell variant={'head'}>TOTAL</TableCell>
              <TableCell variant={'head'}>CAPITAL</TableCell>
              <TableCell variant={'head'}>INCOME</TableCell>
              <TableCell variant={'head'}>TOTAL</TableCell>
              <TableCell variant={'head'}>CAPITAL</TableCell>
              <TableCell variant={'head'}>INCOME</TableCell>
              <TableCell variant={'head'}>TOTAL</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={uuidv4()} sx={getBodyRowSx(index)}>
                <TableCell>
                  <Typography fontWeight="bold">{row.label}</Typography>
                </TableCell>
                <TableCell>
                  <Typography color={getValueColor(row.capitalReturn)}>{formatPercentage(row.capitalReturn, '-')}</Typography>
                </TableCell>
                <TableCell>
                  <Typography color={getValueColor(row.incomeReturn)}>{formatPercentage(row.incomeReturn, '-')}</Typography>
                </TableCell>
                <TableCell>
                  <Typography color={getValueColor(row.totalReturn)}>{formatPercentage(row.totalReturn, '-')}</Typography>
                </TableCell>
                <TableCell>
                  <Typography color={getValueColor(row.benchmarkCapitalReturn)}>{formatPercentage(row.benchmarkCapitalReturn, '-')}</Typography>
                </TableCell>
                <TableCell>
                  <Typography color={getValueColor(row.benchmarkIncomeReturn)}>{formatPercentage(row.benchmarkIncomeReturn, '-')}</Typography>
                </TableCell>
                <TableCell>
                  <Typography color={getValueColor(row.benchmarkTotalReturn)}>{formatPercentage(row.benchmarkTotalReturn, '-')}</Typography>
                </TableCell>
                <TableCell>
                  <Typography color={getValueColor(row.portfolioVsBenchmarkCapitalReturn)}>
                    {formatPercentage(row.portfolioVsBenchmarkCapitalReturn, '-')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography color={getValueColor(row.portfolioVsBenchmarkIncomeReturn)}>
                    {formatPercentage(row.portfolioVsBenchmarkIncomeReturn, '-')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography color={getValueColor(row.portfolioVsBenchmarkTotalReturn)}>
                    {formatPercentage(row.portfolioVsBenchmarkTotalReturn, '-')}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </LoadingIndicator>
  );
};
